import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'

import { useBorderBoxSizeCallback } from '@blade/ui-kit'
import { DomUtil } from '@root/shared/core'

import type React from 'react'

export type DetectOverflowChildrenProps = {
  horizontalOverflow: boolean
  verticalOverflow: boolean
  overflowInlineStart: boolean
  overflowInlineEnd: boolean
  overflowBlockStart: boolean
  overflowBlockEnd: boolean
  ref: React.MutableRefObject<HTMLElement | null>
}

type DetectOverflowProps = {
  children: (props: DetectOverflowChildrenProps) => React.ReactNode
}

type OverflowEdges = Omit<DetectOverflowChildrenProps, 'ref'>

const INITIAL_EDGES: OverflowEdges = {
  horizontalOverflow: false,
  verticalOverflow: false,
  overflowInlineStart: false,
  overflowInlineEnd: false,
  overflowBlockStart: false,
  overflowBlockEnd: false,
}

const readOverflowEdges = (element: HTMLElement): OverflowEdges => {
  const edges = DomUtil.getElementScrollEdges(element)
  return {
    ...edges,
    horizontalOverflow: edges.overflowInlineStart || edges.overflowInlineEnd,
    verticalOverflow: edges.overflowBlockStart || edges.overflowBlockEnd,
  }
}

export const DetectOverflow: React.FC<DetectOverflowProps> = ({ children }) => {
  const [edges, setEdges] = useState<OverflowEdges>(INITIAL_EDGES)
  const ref = useRef<HTMLElement | null>(null)

  const childEl = children({
    ref: ref,
    ...edges,
  })

  const detectOverflow = useCallback(() => {
    const element = ref.current
    if (!element) {
      return
    }

    const next = readOverflowEdges(element)
    setEdges((prev) =>
      prev.horizontalOverflow === next.horizontalOverflow &&
      prev.verticalOverflow === next.verticalOverflow &&
      prev.overflowInlineStart === next.overflowInlineStart &&
      prev.overflowInlineEnd === next.overflowInlineEnd &&
      prev.overflowBlockStart === next.overflowBlockStart &&
      prev.overflowBlockEnd === next.overflowBlockEnd
        ? prev
        : next,
    )
  }, [])

  useLayoutEffect(() => detectOverflow(), [detectOverflow])
  useEffect(() => detectOverflow(), [detectOverflow])

  useEffect(() => {
    const element = ref.current
    if (!element) {
      return
    }

    const observer = new MutationObserver(() => detectOverflow())
    observer.observe(element, { childList: true, subtree: true })
    element.addEventListener('scroll', detectOverflow, { passive: true })
    return () => {
      observer.disconnect()
      element.removeEventListener('scroll', detectOverflow)
    }
  }, [detectOverflow])

  useBorderBoxSizeCallback(ref, () => {
    detectOverflow()
  })

  return childEl
}
